import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import "./essentiel.css";
import useIsMountedRef from "../../utils/mountedRef";

const alphabet = "ABCDEILMNOPQRSTVW".split("");

const isMobileEssentielLayout = () => {
  const sidebar = document.querySelector(".sidebar-container");
  return sidebar && window.getComputedStyle(sidebar).flexDirection === "column";
};

const scrollToActeContent = (selector) => {
  if (!isMobileEssentielLayout()) {
    return;
  }

  window.requestAnimationFrame(() => {
    const target = document.querySelector(selector);
    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  });
};

export default function MENU_EMOLACTES({ setActiveItem, setSelectedItems, setSimulateurResponses, currentTabFromURL, onReady }) {
  const [menuItems, setMenuItems] = useState([]);
  const [activeLetter, setActiveLetter] = useState("A"); // "A" est actif par défaut
  const [activeItem, setActiveItemLocal] = useState(null);
  const menuRef = useRef(null); // Référence pour le menu défilant
  const alphabetRef = useRef(null); // Référence pour la colonne des lettres
  const isMountedRef = useIsMountedRef();

  const getTargetItemFromURL = (items, target) => {
    if (!target) {
      return null;
    }

    if (target.startsWith("id:")) {
      const id = decodeURIComponent(target.replace("id:", ""));
      return items.find((item) => String(item.id) === id) || null;
    }

    if (target.startsWith("acte:")) {
      const nActe = decodeURIComponent(target.replace("acte:", ""));
      return items.find((item) => String(item.n_acte) === nActe) || null;
    }

    const rowIndex = parseInt(target, 10);
    return Number.isNaN(rowIndex) ? null : items[rowIndex] || null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const jwt = user?.jwt;

        const response = await axios.get(`${API_BASE_URL}/essentiel_emol`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwt,
          },
        });

        const data = response.data;
        if (Array.isArray(data)) {
          setMenuItems(data);

          if (currentTabFromURL.length > 0) {
            const targetItem = getTargetItemFromURL(data, currentTabFromURL[1]);
            if (targetItem) {
              handleMenuItemClick(targetItem, data);
            }
            return;
          }
          if (!activeItem && data.length > 0) {
            setActiveItemLocal(data[0], data[1]);
            setActiveItem(data[0], data[1]);
            setSelectedItems([data[0], data[1]]);
          }
        }
      } catch (error) {
        console.error("Error fetching data", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("user");
          if (error.response.data?.error === "Other_user_connected") {
            localStorage.setItem("other_user_connected", "other_user_connected");
          } else {
            sessionStorage.setItem("session_expired", "true");
          }
          window.location.replace("/login");
        }
      } finally {
        onReady?.();
      }
    };

    fetchData();
  }, [isMountedRef, currentTabFromURL]);

  const uniqueMenuItems = Array.from(new Set(menuItems.map((item) => item.n_acte))).map((n_acte) => {
    return menuItems.find((item) => item.n_acte === n_acte);
  });

  const handleMenuItemClick = (item, datamenu) => {
    if (!item) {
      return;
    }

    setActiveItemLocal(item);
    setActiveItem(item);
    setSimulateurResponses({});

    // Mettre à jour la lettre active
    setActiveLetter(item.letter);

    // Faire défiler la colonne des lettres pour centrer la lettre correspondante
    const letterIndex = alphabet.indexOf(item.letter);
    if (letterIndex !== -1) {
      const alphabetContainer = alphabetRef.current;
      const letterElement = alphabetContainer.children[letterIndex];
      if (letterElement) {
        const letterTopPosition = letterElement.offsetTop;
        alphabetContainer.scrollTo({
          top: letterTopPosition - alphabetContainer.clientHeight / 2,
          behavior: "smooth",
        });
      }
    }
    if (menuItems.length > 0) {
      const filteredItems = menuItems.filter((menuItem) => menuItem.n_acte === item.n_acte);
      setSelectedItems(filteredItems);
    } else {
      const filteredItems = datamenu.filter((menuItem) => menuItem.n_acte === item.n_acte);
      setSelectedItems(filteredItems);
    }

    scrollToActeContent(".emol_global_card");
  };

  const handleScroll = () => {
    if (!menuRef.current) {
      return;
    }

    const letterHeaders = Array.from(menuRef.current.querySelectorAll(".group-letter h2"));
    if (letterHeaders.length === 0) {
      return;
    }

    const scrollPosition = menuRef.current.scrollTop;
    const maxScroll = menuRef.current.scrollHeight - menuRef.current.clientHeight;

    if (scrollPosition >= maxScroll - 10) {
      const lastLetter = letterHeaders[letterHeaders.length - 1].getAttribute("data-letter");
      setActiveLetter(lastLetter);
      return;
    }

    const targetOffset = scrollPosition + 72;
    let currentLetter = letterHeaders[0].getAttribute("data-letter");

    letterHeaders.forEach((element) => {
      if (element.offsetTop <= targetOffset) {
        currentLetter = element.getAttribute("data-letter");
      }
    });

    setActiveLetter(currentLetter);
  };

  useEffect(() => {
    const menuElement = menuRef.current;
    if (menuElement) {
      menuElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (menuElement) {
        menuElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [menuItems]);

  useEffect(() => {
    handleScrollToLetter("A");
  }, []);

  const handleScrollToLetter = (letter) => {
  const groupLetterElements = document.querySelectorAll(".group-letter h2");
  let targetGroupElement = null;
  
  for (const el of groupLetterElements) {
    if (el.textContent.trim() === letter) {
      targetGroupElement = el.closest(".group-letter");
      break;
    }
  }

  if (targetGroupElement) {
    const menuTop = menuRef.current.offsetTop;
    const elementTopPosition = targetGroupElement.offsetTop - menuTop - 8;

    menuRef.current.scrollTo({
      top: elementTopPosition,
      behavior: "auto",
    });
  } else {
    const element = document.querySelector(`.menu-item[data-letter='${letter}']`);
    if (element) {
      const menuTop = menuRef.current.offsetTop;
      menuRef.current.scrollTo({
        top: element.offsetTop - menuTop - 8,
        behavior: "auto",
      });
    }
  }
  
  setActiveLetter(letter);
};

  // fonction permettant le scroll sur l'acte en fonction de l'index récupéré dans l'url
  useEffect(() => {
    if (menuItems.length > 0) {
      const scrollToActeFromURL = () => {
        const hashParts = window.location.hash.split("#");
        if (hashParts.length > 2) {
          const targetItem = getTargetItemFromURL(menuItems, hashParts[2]);

          if (!targetItem) {
            return;
          }

          const targetElement = Array.from(document.querySelectorAll(".menu-item")).find(
            (element) => element.dataset.nActe === String(targetItem.n_acte)
          );

          if (targetElement) {
            const menuTop = menuRef.current.offsetTop;
            const elementTop = targetElement.offsetTop;

            menuRef.current.scrollTo({
              top: elementTop - menuTop - 20,
              behavior: "smooth",
            });
          }

          // Reuse the standard selection flow so the left menu and right table stay in sync.
          handleMenuItemClick(targetItem, menuItems);
        }
      };

      scrollToActeFromURL();
    }
  }, [menuItems]);

  return (
    <div className="sidebar-container">
      <div className="alphabet-column" ref={alphabetRef}>
        {alphabet.map((letter) => (
          <React.Fragment key={letter}>
            <div
              className={`alphabet-letter ${activeLetter === letter ? "active" : ""}`}
              onClick={() => handleScrollToLetter(letter)}
            >
              {letter}
            </div>
            {letter !== alphabet[alphabet.length - 1] && <div className="letter-separator"></div>}
          </React.Fragment>
        ))}
      </div>
      <div className="menu-column" ref={menuRef}>
        {uniqueMenuItems.length > 0 ? (
          uniqueMenuItems.map((item, index) => {
            const prevItem = uniqueMenuItems[index - 1];
            const showLetter = index === 0 || item.letter !== prevItem.letter;
            const rowIndex = menuItems.findIndex((menuItem) => menuItem === item);

            return (
              <React.Fragment key={item.n_acte}>
                {showLetter && (
                  <div className="group-letter">
                    <h2
                      data-letter={item.letter}
                      style={{
                        borderTop: index > 0 && item.letter !== prevItem.letter ? "3px solid #016954" : "",
                      }}
                    >
                      {item.letter}
                    </h2>
                  </div>
                )}
                <div
                  className={`menu-item ${activeItem?.n_acte === item.n_acte ? "active" : ""}`}
                  onClick={() => handleMenuItemClick(item)}
                  data-letter={item.letter}
                  data-row-index={rowIndex}
                  data-n-acte={item.n_acte}
                >
                  <span dangerouslySetInnerHTML={{ __html: item.actes_mef }} />
                </div>
              </React.Fragment>
            );
          })
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
